<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-8 align-center">
        <h2 class="text-capitalize">
          {{ $t("finance.dashboard.title") }}
        </h2>
        <v-spacer></v-spacer>
      </div>

      <v-row class="mt-4">
        <v-col cols="12">
          <v-card class="rounded-lg h-100" outlined>
            <v-card-text>
              <div class="d-flex justify-space-between align-start flex-wrap mb-4">
                <div class="d-flex align-center mb-2">
                  <v-avatar color="indigo lighten-5" rounded size="40" class="mr-3">
                    <v-icon color="indigo">mdi-chart-bar-stacked</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h6 font-weight-bold">{{ $t("finance.dashboard.loanApplicationsChart") }}</div>
                    <div class="text-caption grey--text">{{ $t("finance.dashboard.applicationsByStatus") }}</div>
                  </div>
                </div>
                <v-btn-toggle
                  dense
                  class="elevation-1 mb-2"
                  borderless
                  color="primary"
                  background-color="white"
                  v-model="timeframes.applications"
                  @change="fetchLoanApplications"
                >
                  <v-btn small value="today">{{ $t("finance.reports.today") }}</v-btn>
                  <v-btn small value="week">{{ $t("finance.reports.week") }}</v-btn>
                  <v-btn small value="month">{{ $t("finance.reports.month") }}</v-btn>
                  <v-btn small value="year">{{ $t("finance.reports.year") }}</v-btn>
                </v-btn-toggle>
              </div>

              <div v-if="chartLoading.applications" class="d-flex justify-center py-10">
                <v-progress-circular indeterminate color="primary" />
              </div>
              <apexchart v-else type="bar" height="350" :options="loanAppsChartOptions" :series="loanAppsSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="8">
          <v-card class="rounded-lg" outlined>
            <v-card-text>
              <div class="d-flex justify-space-between align-start flex-wrap mb-4">
                <div class="d-flex align-center mb-2">
                  <v-avatar color="teal lighten-5" rounded size="40" class="mr-3">
                    <v-icon color="teal">mdi-chart-line</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h6 font-weight-bold">{{ $t("finance.dashboard.averageLoanAmountTrend") }}</div>
                    <div class="text-caption grey--text">{{ $t("finance.dashboard.loanAmountOverTime") }}</div>
                  </div>
                </div>
                <v-btn-toggle
                  dense
                  class="elevation-1 mb-2"
                  borderless
                  color="primary"
                  background-color="white"
                  v-model="timeframes.average"
                  @change="fetchAverage"
                >
                  <v-btn small value="today">{{ $t("finance.reports.today") }}</v-btn>
                  <v-btn small value="week">{{ $t("finance.reports.week") }}</v-btn>
                  <v-btn small value="month">{{ $t("finance.reports.month") }}</v-btn>
                  <v-btn small value="year">{{ $t("finance.reports.year") }}</v-btn>
                </v-btn-toggle>
              </div>

              <div v-if="chartLoading.average" class="d-flex justify-center py-10">
                <v-progress-circular indeterminate color="primary" />
              </div>
              <apexchart v-else type="area" height="350" :options="avgLoanChartOptions" :series="avgLoanSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="rounded-lg h-100" outlined>
            <v-card-text>
              <div class="d-flex justify-space-between align-start flex-wrap mb-4">
                <div class="d-flex align-center mb-2">
                  <v-avatar color="orange lighten-5" rounded size="40" class="mr-3">
                    <v-icon color="orange">mdi-map-marker-radius</v-icon>
                  </v-avatar>
                  <div>
                    <div class="text-h6 font-weight-bold">{{ $t("finance.dashboard.applicationsByRegion") }}</div>
                    <div class="text-caption grey--text">{{ $t("finance.dashboard.regionBreakdown") }}</div>
                  </div>
                </div>
                <v-btn-toggle
                  dense
                  class="elevation-1 mb-2"
                  borderless
                  color="primary"
                  background-color="white"
                  v-model="timeframes.regions"
                  @change="fetchRegions"
                >
                  <v-btn small value="today">{{ $t("finance.reports.today") }}</v-btn>
                  <v-btn small value="week">{{ $t("finance.reports.week") }}</v-btn>
                  <v-btn small value="month">{{ $t("finance.reports.month") }}</v-btn>
                  <v-btn small value="year">{{ $t("finance.reports.year") }}</v-btn>
                </v-btn-toggle>
              </div>

              <div v-if="chartLoading.regions" class="d-flex justify-center py-10">
                <v-progress-circular indeterminate color="primary" />
              </div>
              <apexchart v-else type="donut" height="350" :options="regionChartOptions" :series="regionSeries"></apexchart>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-if="chartLoading.metrics" class="mt-8 mb-4">
        <v-col cols="12" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </v-col>
      </v-row>
      <v-row v-else class="mt-8 mb-4">
        <v-col cols="12" md="4">
          <v-card class="pa-4 rounded-lg" outlined>
            <div class="d-flex justify-start align-center">
              <v-avatar color="primary" size="48">
                <v-icon color="white">mdi-currency-usd</v-icon>
              </v-avatar>
              <div class="ml-4">
                <div class="text-subtitle-1 grey--text">{{ $t("finance.dashboard.avgLoanAmount") }}</div>
                <div class="text-h4 font-weight-bold mt-1">
                  ${{ metrics.avgLoanAmountThisMonth.value.toLocaleString() }}
                </div>
              </div>
            </div>
            <div class="mt-4 d-flex align-center">
              <v-icon :color="metrics.avgLoanAmountThisMonth.trend === 'up' ? 'success' : 'error'" small>
                {{ metrics.avgLoanAmountThisMonth.trend === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <span :class="metrics.avgLoanAmountThisMonth.trend === 'up' ? 'success--text' : 'error--text'" class="ml-1 font-weight-bold">
                {{ Math.abs(metrics.avgLoanAmountThisMonth.change) }}%
              </span>
              <span class="grey--text ml-2 text-caption">{{ $t("finance.dashboard.vsLastMonth") }}</span>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="pa-4 rounded-lg" outlined>
            <div class="d-flex justify-start align-center">
              <v-avatar color="primary" size="48">
                <v-icon color="white">mdi-file-document-multiple</v-icon>
              </v-avatar>
              <div class="ml-4">
                <div class="text-subtitle-1 grey--text">{{ $t("finance.dashboard.totalApplications") }}</div>
                <div class="text-h4 font-weight-bold mt-1">
                  {{ metrics.totalApplicationsThisMonth.value.toLocaleString() }}
                </div>
              </div>
            </div>
            <div class="mt-4 d-flex align-center">
              <v-icon :color="metrics.totalApplicationsThisMonth.trend === 'up' ? 'success' : 'error'" small>
                {{ metrics.totalApplicationsThisMonth.trend === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <span :class="metrics.totalApplicationsThisMonth.trend === 'up' ? 'success--text' : 'error--text'" class="ml-1 font-weight-bold">
                {{ Math.abs(metrics.totalApplicationsThisMonth.change) }}%
              </span>
              <span class="grey--text ml-2 text-caption">{{ $t("finance.dashboard.vsLastMonth") }}</span>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="pa-4 rounded-lg" outlined>
            <div class="d-flex justify-start align-center">
              <v-avatar color="primary" size="48">
                <v-icon color="white">mdi-check-decagram</v-icon>
              </v-avatar>
              <div class="ml-4">
                <div class="text-subtitle-1 grey--text">{{ $t("finance.dashboard.approvalRate") }}</div>
                <div class="text-h4 font-weight-bold mt-1">
                  {{ metrics.approvalPercentageThisMonth.value }}%
                </div>
              </div>
            </div>
            <div class="mt-4 d-flex align-center">
              <v-icon :color="metrics.approvalPercentageThisMonth.trend === 'up' ? 'success' : 'error'" small>
                {{ metrics.approvalPercentageThisMonth.trend === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <span :class="metrics.approvalPercentageThisMonth.trend === 'up' ? 'success--text' : 'error--text'" class="ml-1 font-weight-bold">
                {{ Math.abs(metrics.approvalPercentageThisMonth.change) }}%
              </span>
              <span class="grey--text ml-2 text-caption">{{ $t("finance.dashboard.vsLastMonth") }}</span>
            </div>
          </v-card>
        </v-col>
      </v-row>

    </v-container>
  </div>
</template>

<script>
import Breadcrumb from "@/components/core/Breadcrumb";
import loadingMixin from "@/mixins/LoadingMixin";
import VueApexCharts from "vue-apexcharts";
import FinanceService from "@/_services/FinanceService";

export default {
  name: "FinanceDashboard",
  components: {
    breadcrumb: Breadcrumb,
    apexchart: VueApexCharts,
  },
  mixins: [loadingMixin],
  data() {
    return {
      timeframes: {
        applications: "month",
        regions: "month",
        average: "month"
      },
      chartLoading: {
        metrics: true,
        applications: true,
        regions: true,
        average: true,
      },
      metrics: {
        avgLoanAmountThisMonth: { value: 0, change: 0, trend: "up" },
        totalApplicationsThisMonth: { value: 0, change: 0, trend: "up" },
        approvalPercentageThisMonth: { value: 0, change: 0, trend: "up" },
      },
      loanAppsSeries: [{ data: [] }],
      loanAppsChartOptions: {
        chart: { type: "bar", stacked: true, toolbar: { show: true } },
        colors: ["#4CAF50", "#FFC107", "#2196F3", "#F44336"],
        xaxis: { categories: [] },
        legend: { position: "top" },
        fill: { opacity: 1 },
      },
      regionSeries: [],
      regionChartOptions: {
        chart: { type: "donut" },
        labels: [],
        colors: ["#3f51b5", "#00bcd4", "#ff9800", "#e91e63", "#9c27b0", "#8bc34a"],
        plotOptions: {
          pie: { donut: { size: '65%' } }
        },
        dataLabels: { enabled: false },
        legend: { position: "bottom" }
      },
      avgLoanSeries: [{ data: [] }], 
      avgLoanChartOptions: {
        chart: { type: "area", toolbar: { show: true } },
        colors: ["#009688"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        xaxis: { categories: [] },
        yaxis: {
          labels: {
            formatter: (value) => { return "$" + value.toLocaleString() }
          }
        },
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 90, 100] }
        }
      },
    };
  },
  computed: {
    // Moved breadcrumbs to computed so this.$t works correctly
    breadcrumbs() {
      return [
        {
          text: this.$t("dashboard.title"),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t("dashboard.title"),
          disabled: true,
          to: "/finance/dashboard",
        },
      ];
    }
  },
  mounted() {
    this.fetchDashboardData();
  },
  methods: {
    fetchDashboardData() {
      // Trigger all fetches simultaneously
      this.getMetrics();
      this.fetchLoanApplications();
      this.fetchRegions();
      this.fetchAverage();
    },

    async getMetrics() {
      this.chartLoading.metrics = true;
      try {
        const res = await FinanceService.getDashboardMetrics();
        if (res.success) {
          this.metrics = res.data;
        }
      } catch (err) {
        console.error("Error fetching metrics:", err);
      } finally {
        this.chartLoading.metrics = false;
      }
    },

    async fetchLoanApplications() {
      this.chartLoading.applications = true;
      try {
        const params = { timeframe: this.timeframes.applications };
        const res = await FinanceService.getLoanApplicationsChart(params);
        if (res.success) {
          const chartData = res.data.chartData;
          
          this.loanAppsChartOptions = {
            ...this.loanAppsChartOptions,
            xaxis: {
              categories: chartData.map(item => item.label)
            }
          };

          this.loanAppsSeries = [
            { name: this.$t("finance.status.approved"), data: chartData.map(item => item.approved) },
            { name: this.$t("finance.status.pending"), data: chartData.map(item => item.pending) },
            { name: this.$t("finance.status.waiting"), data: chartData.map(item => item.waiting) },
            { name: this.$t("finance.status.rejected"), data: chartData.map(item => item.rejected) },
          ];
        }
      } catch (err) {
        console.error("Error fetching loan applications:", err);
      } finally {
        this.chartLoading.applications = false;
      }
    },

    async fetchRegions() {
      this.chartLoading.regions = true;
      try {
        const params = { timeframe: this.timeframes.regions };
        const res = await FinanceService.getApplicationsByRegionChart(params);
        if (res.success) {
          const regionsData = res.data.regions;
          this.regionChartOptions = {
            ...this.regionChartOptions,
            labels: regionsData.map(item => item.region || "Unknown")
          };
          this.regionSeries = regionsData.map(item => item.count);
        }
      } catch (err) {
        console.error("Error fetching regions:", err);
      } finally {
        this.chartLoading.regions = false;
      }
    },

    async fetchAverage() {
      this.chartLoading.average = true;
      try {
        const params = { timeframe: this.timeframes.average };
        const res = await FinanceService.getAverageLoanAmountChart(params);
        if (res.success) {
          const chartData = res.data.chartData;
          
          this.avgLoanChartOptions = {
            ...this.avgLoanChartOptions,
            xaxis: {
              categories: chartData.map(item => item.label)
            }
          };

          this.avgLoanSeries = [
            {
              name: this.$t("dashboard.avgAmount"),
              data: chartData.map(item => parseFloat(item.avgAmount))
            }
          ];
        }
      } catch (err) {
        console.error("Error fetching average loan amounts:", err);
      } finally {
        this.chartLoading.average = false;
      }
    }
  }
};
</script>

<style scoped>
.h-100 {
  height: 100%;
}
</style>